import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { filter } from 'rxjs';
import { AuthService } from './auth/auth.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import {
  IMPERSONATION_PROVIDER,
  IMPERSONATION_TOKEN,
} from '@auth/custom-providers/impersonation-provider';
import { AppInsightsService } from '@data-access/shared';
import { AuthActions } from './auth/redux/auth.actions';
import { SystemControlNotificationEventHandler } from '@data-access/system-controls/communication';
import { Router, NavigationEnd } from '@angular/router';
import { WebSocketActions } from './websocket/redux/websocket.actions';
import { FEATURE_FLAGS_SIGNAL_STORE } from '@data-access/system-controls/feature-flags';

@Component({
  providers: [IMPERSONATION_PROVIDER],
  selector: 'wcg-root',
  standalone: false,
  styleUrls: ['./app.component.scss'],
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  private _authService = inject(AuthService);
  private _router: Router = inject(Router);
  private readonly _session = inject<{ remove: () => void }>(
    IMPERSONATION_TOKEN,
  );
  private readonly _appInsightsService = inject(AppInsightsService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly store = inject(Store<any>);
  private _systemControlHandler = inject(SystemControlNotificationEventHandler);
  private _featureFlagsStore = inject(FEATURE_FLAGS_SIGNAL_STORE);

  readonly $isAuthenticated = toSignal(this._authService.isAuthenticated$);
  readonly $isLoadingUser = toSignal(this._authService.loadingUser$);
  readonly $isImpersonating = signal<boolean>(false);
  readonly $impersonationText = signal<string>('');
  readonly $isMaintenancePage = signal<boolean>(
    // Check immediately if we're on maintenance page
    this._router.url.includes('/maintenance'),
  );
  readonly $showTalkdesk = computed(() => {
    return (
      this.$isAuthenticated() &&
      !this.$isLoadingUser() &&
      !this.$isImpersonating() &&
      !this.$isMaintenancePage()
    );
  });

  constructor() {
    this._appInsightsService.init();
  }

  ngOnInit() {
    // Track if we're on the maintenance page
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((event: any) => {
        this.$isMaintenancePage.set(event.url.includes('/maintenance'));
      });

    this.store
      .select((state: { user: { currentUser: any } }) => state.user.currentUser)
      .pipe(
        filter((user) => !!user && !this.$isMaintenancePage()),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((user: any) => {
        // Load feature flags after user authentication
        this._featureFlagsStore.loadFeatureFlags();

        // Update impersonation signals
        this.$isImpersonating.set(user.is_impersonating);
        if (user.is_impersonating) {
          this.$impersonationText.set(
            `You (${user.impersonator_name}) are impersonating user "${user.impersonatee_name}"`,
          );
        } else {
          this.$impersonationText.set('');
        }

        // start legacy websocket connection
        this.store.dispatch(WebSocketActions.connectWebSocket());

        // Initialize settings communication (maintenance mode, system announcements, etc.)
        this._systemControlHandler
          .startHandling(user.id)
          .pipe(takeUntilDestroyed(this._destroyRef))
          .subscribe({
            error: (err) => console.error('WebSocket notification error:', err),
          });
      });
  }

  endImpersonation() {
    this.store.dispatch(AuthActions.endImpersonationRequested());
  }
}
